import * as React from 'react';
import { JsonFormsDispatch, useJsonForms } from '@jsonforms/react';
import { StackLayout, Box, VisuallyHidden } from '@leagueplatform/genesis-core';
import { Generate, composePaths, Actions } from '@jsonforms/core';
import {
  computeSchema,
  applyConditionalLogic,
} from '@web-config-app/schema-utils';
import { Accordion } from '@leagueplatform/web-common-components';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { getAllFeatureFlags, unsetObjectProperty } from '@web-config-app/core';
import { useLocales } from '@web-config-app/core-react';
import type {
  ArrayControl,
  EntityFormArrayControlProps,
  PrimitiveObjectArrayControlItem,
  PrimitiveObjectArrayItemProps,
  SortableItemRenderProps,
} from '@web-config-app/entity-form';
import {
  createDefaultValueForSchema,
  EntityFormArrayControl,
  ArrayAddButton,
  CombinatorArrayAddActionMenu,
  CompoundPrimitiveGroupFieldset,
  addCombinatorOptionToArray,
  getDataPointValue,
  useArrayControlCombinatorHelpers,
  EmptyArrayState,
  useLocalizedArrayOperations,
  useEntityDetailsProps,
  moveItemToIndex,
  useJsonFormsErrorsForPath,
} from '@web-config-app/entity-form';
import { throttle } from 'lodash-es';
import { PrimitiveObjectArrayItem } from './primitive-object-array-item.component';
import { PrimitiveObjectArrayItemWithEntityReference } from './primitive-object-array-item-with-entity-reference.component';

/**
 * This control renders for Arrays of Object Primitive type - excluding primitive arrays
 * On move / up / down and delete all accordions are closed
 *
 * Creates a uiSchema for children of type 'PrimitiveObjectArrayItem' so that the object is rendered with correct ObjectPrimitiveArrayItemLayout
 *
 */

const getEntityReferencePropertySchema = (
  schema: AnnotatedJsonSchema | undefined,
  data: any,
) => {
  const entityReferencePropertyEntries =
    schema &&
    Object.entries(schema.properties ?? {}).filter(
      ([, propertySchema]: [string, AnnotatedJsonSchema]) =>
        Boolean(propertySchema['x-entity-reference']),
    );
  if (
    !entityReferencePropertyEntries ||
    entityReferencePropertyEntries.length !== 1
  )
    return null;
  const [dataIdProperty, entityReferencePropertySchema] =
    entityReferencePropertyEntries[0];
  const referenceInstanceId = dataIdProperty && data?.[dataIdProperty];
  return { referenceInstanceId, schema: entityReferencePropertySchema };
};

export const ArrayOfPrimitiveObjectsControlContents = ({
  path,
  schema,
  rootSchema,
  data,
  enabled,
  cells,
  renderers,
  id,
  banner,
  hint,
  label,
  addItem: _addItem,
  removeItems,
  moveDown,
  moveUp,
  arrayAddLabel,
  itemLabel: defaultItemLabel,
  itemLabelPropertyRef,
  visuallyHiddenItemLabel,
  statusMessage,
  inputStatus,
  combinatorProperties,
  handleUpdateArrayItem,
  localized,
  createSortableId,
  SortableItem,
  SortableWrapper,
  registerAfterReorderCallback,
}: EntityFormArrayControlProps) => {
  const ctx = useJsonForms();
  const { arrayItemHasError } = useJsonFormsErrorsForPath(path);
  const { dispatch } = ctx;
  const { entity, formPath, entityRootData, isUsingConfigAppBackend } =
    useEntityDetailsProps();

  const rootEntityAttributes = isUsingConfigAppBackend
    ? entityRootData?.attributes?.domainEntityAttributes
    : entityRootData?.attributes;
  const { locales } = useLocales();
  const isLocalizable =
    localized && locales && locales.length > 1 && entity.localizable;

  // localized array operations will only be used if isLocalizable is true
  const {
    handleLocalizedMoveUp,
    handleLocalizedMoveDown,
    handleLocalizedRemoveItem,
    addLocalizedArrayItem,
    handleLocalizedDuplicateItem,
    handleLocalizedMoveToPosition,
  } = useLocalizedArrayOperations({
    path,
    formPath,
    placeholderValue: {},
  });

  const [itemsOpen, setItemsOpen] = React.useState<string[]>([]);

  const handleAccordionValueChange = (newValues: string[]) => {
    setItemsOpen(newValues);
  };

  /**
   * Function to close all accordion items anytime the data length or order changes
   */
  const closeAllAccordionItems = React.useCallback(() => {
    setItemsOpen([]);
  }, []);

  // Store the latest callback functions and state in refs to avoid re-registering on every render
  const itemsOpenRef = React.useRef(itemsOpen);
  const handleLocalizedMoveToPositionRef = React.useRef(
    handleLocalizedMoveToPosition,
  );
  const isLocalizableRef = React.useRef(isLocalizable);

  // Update refs when values change
  React.useEffect(() => {
    itemsOpenRef.current = itemsOpen;
    handleLocalizedMoveToPositionRef.current = handleLocalizedMoveToPosition;
    isLocalizableRef.current = isLocalizable;
  });

  // Register custom after-reorder logic for this array control - only once on mount
  React.useEffect(() => {
    registerAfterReorderCallback((fromIndex: number, toIndex: number) => {
      // Preserve accordion open state by mapping old indices to new indices
      const openIndices = itemsOpenRef.current
        .map((openId) => {
          const match = openId.match(/-(\d+)$/);
          return match ? parseInt(match[1], 10) : -1;
        })
        .filter((idx) => idx !== -1);

      // Map old indices to new indices after reorder
      const newOpenIndices = openIndices.map((oldIdx) => {
        if (oldIdx === fromIndex) {
          return toIndex;
        }

        if (fromIndex < toIndex && oldIdx > fromIndex && oldIdx <= toIndex) {
          return oldIdx - 1;
        }

        if (fromIndex > toIndex && oldIdx >= toIndex && oldIdx < fromIndex) {
          return oldIdx + 1;
        }
        return oldIdx;
      });

      // Update open items with new IDs
      setItemsOpen(newOpenIndices.map((idx) => `${id}-${idx}`));

      // Handle localized arrays - move the translated items to match the default locale
      if (isLocalizableRef.current) {
        handleLocalizedMoveToPositionRef.current({ fromIndex, toIndex });
      }
    });
  }, [registerAfterReorderCallback, id]); // Only depends on registerAfterReorderCallback which is stable

  const handleDuplicateItem = React.useCallback(
    (itemData: any, index: number) => () => {
      _addItem(path, itemData)();
      if (isLocalizable && Array.isArray(data)) {
        if (index >= 0) {
          // Duplicate this item in all localized arrays
          handleLocalizedDuplicateItem({ index });
        }
      }
    },
    [_addItem, data, handleLocalizedDuplicateItem, isLocalizable, path],
  );

  const handleRemoveItem = React.useCallback(
    (itemPath: string, index: any) => () => {
      closeAllAccordionItems();
      removeItems(itemPath, [index])();
      if (isLocalizable) {
        handleLocalizedRemoveItem({ index });
      }
    },
    [
      closeAllAccordionItems,
      handleLocalizedRemoveItem,
      isLocalizable,
      removeItems,
    ],
  );

  const handleOnMoveUp = React.useCallback(
    (itemPath: string, index: number) => () => {
      closeAllAccordionItems();
      moveUp(itemPath, index)();
      if (isLocalizable) {
        handleLocalizedMoveUp({ index });
      }
    },
    [closeAllAccordionItems, handleLocalizedMoveUp, isLocalizable, moveUp],
  );

  const handleOnMoveDown = React.useCallback(
    (itemPath: string, index: number) => () => {
      closeAllAccordionItems();
      moveDown(itemPath, index)();
      if (isLocalizable) {
        handleLocalizedMoveDown({ index });
      }
    },
    [closeAllAccordionItems, handleLocalizedMoveDown, isLocalizable, moveDown],
  );

  const handleMoveToPosition = React.useCallback(
    (itemPath: string, fromIndex: number) => (toIndex: number) => {
      const newArray = moveItemToIndex(data as any[], fromIndex, toIndex);
      dispatch?.(Actions.update(itemPath, () => newArray));
      if (isLocalizable) {
        handleLocalizedMoveToPosition({ fromIndex, toIndex });
      }
    },
    [data, dispatch, handleLocalizedMoveToPosition, isLocalizable],
  );

  const handleCreateDefaultValue = React.useCallback(() => {
    if (isLocalizable) {
      addLocalizedArrayItem();
    }
    return createDefaultValueForSchema(schema) || {};
  }, [addLocalizedArrayItem, isLocalizable, schema]);

  const {
    combinatorActionMenuItems,
    getSelectedSubSchema,
    getLabelForSelectedSubSchema,
  } = useArrayControlCombinatorHelpers(combinatorProperties);

  const featureFlags = getAllFeatureFlags();
  const featureFlagsKey = JSON.stringify(featureFlags);

  const itemsWithLabels: PrimitiveObjectArrayControlItem[] =
    React.useMemo(() => {
      const items = Array.isArray(data) ? data : [];

      // Filter out any null values from the items array
      const definedItems = items.filter((item: any) => item !== null);
      return definedItems.map((item: any, index: number) => {
        const itemNumber = index + 1;
        const isLastItem = definedItems.length === itemNumber;
        const uniqueId = `${id}-${index}`;
        const selectedSubSchema = getSelectedSubSchema(item);
        const subSchemaLabel = getLabelForSelectedSubSchema(selectedSubSchema);

        const itemLabelDataValue = itemLabelPropertyRef
          ? getDataPointValue(item, itemLabelPropertyRef)
          : null;
        const computedLabel = itemLabelDataValue ?? subSchemaLabel;
        const childPath = composePaths(path, `${index}`);

        /**
         * We need to handle conditional rendering of properties within an array of primitive objects
         * directly here as it is much simpler than handling it at the
         *
         * NOTE: conditional rules for fields here MUST use values relative to the array item and will
         * need an update to be able to work with data relative to the root entity data
         */
        const itemSchema = computeSchema(
          selectedSubSchema ?? schema,
          item,
          [applyConditionalLogic],
          {
            path: '',
            rootData: rootEntityAttributes,
            featureFlags,
            onSchemaPropertyRemoved: (
              dataPath: string,
              propertyName: string,
            ) => {
              const { data: newData, updated } = unsetObjectProperty({
                data: item,
                dataPath,
                propertyName,
              });
              if (updated) {
                handleUpdateArrayItem(childPath, newData);
              }
            },
          },
        );

        const itemUiSchema = Generate.uiSchema(
          itemSchema,
          'PrimitiveObjectArrayItem',
          undefined,
          rootSchema,
        );

        return {
          itemLabel: computedLabel ?? defaultItemLabel,
          isLastItem,
          itemNumber,
          uniqueId,
          childPath,
          itemData: data[index],
          schema: itemSchema,
          uischema: itemUiSchema,
          ...item,
        };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps -- featureFlags is a new ref each render; serialized key provides stable comparison
    }, [
      data,
      id,
      getSelectedSubSchema,
      getLabelForSelectedSubSchema,
      itemLabelPropertyRef,
      path,
      schema,
      rootEntityAttributes,
      rootSchema,
      defaultItemLabel,
      handleUpdateArrayItem,
      featureFlagsKey,
    ]);

  const arrayItemsEmpty = itemsWithLabels?.length === 0;

  const throttledAddItem = React.useMemo(
    () =>
      throttle(
        (itemPath: string, itemData: any) => _addItem(itemPath, itemData)(),
        200,
        { leading: true, trailing: false },
      ),
    [_addItem],
  );

  const addItem = React.useCallback(
    (itemPath: string, itemData: any) => () =>
      throttledAddItem(itemPath, itemData),
    [throttledAddItem],
  );

  React.useEffect(
    () => () => {
      throttledAddItem.cancel();
    },
    [throttledAddItem],
  );

  return (
    /**
     * Todo:: A11y audit of this: https://everlong.atlassian.net/browse/CACT-807
     * i'm not sure if this should be a fieldset or not or if the ol should be labelled as well as the li - but they do not seem to be read out by screen readers - i also do not know if i need that visually hidden legend label or if the buttons to open accordions should have the number in them. I have made it so that the screen-reader behavior makes sense to me, but an audit is needed because semantically right now a lot is happening
     *
     */
    <CompoundPrimitiveGroupFieldset
      label={label}
      banner={banner}
      hint={hint}
      inputStatus={inputStatus}
      statusMessage={statusMessage}
    >
      {arrayItemsEmpty ? (
        <EmptyArrayState item={label} />
      ) : (
        <Accordion.Root
          type="multiple"
          value={itemsOpen}
          onValueChange={handleAccordionValueChange}
        >
          <SortableWrapper>
            <StackLayout
              role="list"
              aria-label={label}
              css={{
                marginBlockStart: '$threeQuarters',
                border: '$borderWidths$thin solid $onSurfaceBorderSubdued',
                borderRadius: '$medium',
                paddingInlineStart: '$none',
                // below overrides default styling
                listStyleType: 'none',
              }}
            >
              {itemsWithLabels.map(
                (
                  {
                    uniqueId,
                    schema: itemSchema,
                    uischema: itemUiSchema,
                    isLastItem,
                    itemLabel,
                    itemNumber,
                    childPath,
                    itemData,
                  }: PrimitiveObjectArrayControlItem,
                  index: number,
                ) => {
                  const hasError = arrayItemHasError(index);
                  const referenceData = getEntityReferencePropertySchema(
                    itemSchema,
                    data[index],
                  );
                  const {
                    referenceInstanceId,
                    schema: entityReferencePropertySchema,
                  } = referenceData ?? {};

                  const sortableId = createSortableId(index);
                  return (
                    <SortableItem
                      key={sortableId}
                      id={sortableId}
                      disabled={!enabled}
                      containerCss={{
                        width: '100%',
                        padding: '$half $half $half 0',
                        marginBlockStart: 0,
                        borderBottom: `${
                          isLastItem
                            ? 'none'
                            : '$borderWidths$thin solid $onSurfaceBorderSubdued'
                        }`,
                        '&:first-of-type:last-of-type': {
                          borderRadius: '$medium',
                        },
                        '&:first-of-type': {
                          borderRadius: '$medium $medium 0 0',
                        },
                        '&:last-of-type': {
                          borderRadius: '0 0 $medium $medium',
                        },
                        backgroundColor: `${
                          itemsOpen.includes(uniqueId)
                            ? '$surfaceBackgroundSecondary'
                            : '$surfaceBackgroundPrimary'
                        }`,
                      }}
                    >
                      {(
                        dragHandleProps: SortableItemRenderProps['dragHandleProps'],
                      ) => {
                        const arrayItemProps: PrimitiveObjectArrayItemProps = {
                          uniqueId,
                          itemLabel,
                          itemNumber,
                          itemData,
                          path,
                          handleOnMoveDown,
                          handleOnMoveUp,
                          handleRemoveItem,
                          handleDuplicateItem,
                          index,
                          arrayLength: itemsWithLabels.length,
                          enabled,
                          onMoveToPosition: handleMoveToPosition(path, index),
                          hasError,
                          dragHandleProps,
                        };

                        const renderItemChildren = (
                          <Box
                            as="fieldset"
                            css={{
                              paddingX: '$two',
                              paddingBlockStart: '$two',
                              border: '$none',
                            }}
                          >
                            {/* Todo:: A11y audit to see if we need this:
                              https://everlong.atlassian.net/browse/CACT-807 */}
                            <VisuallyHidden as="legend">
                              {visuallyHiddenItemLabel} {itemNumber}
                            </VisuallyHidden>
                            <JsonFormsDispatch
                              enabled={enabled}
                              schema={itemSchema}
                              uischema={itemUiSchema}
                              path={childPath}
                              key={childPath}
                              renderers={renderers}
                              cells={cells}
                            />
                          </Box>
                        );

                        return (
                          <Box role="listitem" aria-label={itemLabel}>
                            {entityReferencePropertySchema ? (
                              <PrimitiveObjectArrayItemWithEntityReference
                                {...arrayItemProps}
                                entityReferencePropertySchema={
                                  entityReferencePropertySchema
                                }
                                referenceInstanceId={referenceInstanceId}
                              >
                                {renderItemChildren}
                              </PrimitiveObjectArrayItemWithEntityReference>
                            ) : (
                              <PrimitiveObjectArrayItem {...arrayItemProps}>
                                {renderItemChildren}
                              </PrimitiveObjectArrayItem>
                            )}
                          </Box>
                        );
                      }}
                    </SortableItem>
                  );
                },
              )}
            </StackLayout>
          </SortableWrapper>
        </Accordion.Root>
      )}
      {combinatorActionMenuItems?.length ? (
        <CombinatorArrayAddActionMenu
          buttonLabel={arrayAddLabel}
          menuItems={combinatorActionMenuItems}
          onSelectItem={(itemId) => {
            addCombinatorOptionToArray({
              value: itemId,
              combinatorProperties,
              path,
              addItem,
            });
            if (isLocalizable) {
              addLocalizedArrayItem();
            }
          }}
        />
      ) : (
        <ArrayAddButton
          addItem={addItem}
          path={path}
          createDefaultValue={handleCreateDefaultValue}
          addButtonLabel={arrayAddLabel}
          disabled={!enabled}
        />
      )}
    </CompoundPrimitiveGroupFieldset>
  );
};

const renderArrayControl = (formContentsProps: EntityFormArrayControlProps) => (
  <ArrayOfPrimitiveObjectsControlContents {...formContentsProps} />
);

export const ArrayOfPrimitiveObjectsControl: ArrayControl = (props) => (
  <EntityFormArrayControl {...props} renderControl={renderArrayControl} />
);
